import React, { useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useQueryClient } from '@tanstack/react-query';
import type { EpicSummary } from '../../../../shared/types/epics';
import { isComplete } from '../../../../shared/lib/epicArchive';
import { archiveEpic, unarchiveEpic } from '../../lib/api/epics';

/**
 * FORGE-159: per-row archive / unarchive control for the epics list.
 *
 * - Archived (or auto-hidden) row → "Unarchive" button.
 * - Active row → "Archive" button. If the epic isn't complete, archiving opens a
 *   modal that REQUIRES a reason (submit disabled until non-empty); complete
 *   epics archive with a single confirm. Invalidates the epics queries on success.
 */
export const ArchiveControl: React.FC<{ epic: EpicSummary }> = ({ epic }) => {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  // Drag-selecting text in the textarea and releasing the mouse outside the dialog
  // fires a `click` on the backdrop; only close when the press ALSO started there.
  const downOnBackdrop = useRef(false);

  const archived = epic.archive?.archived ?? false;
  const needsReason = !isComplete(epic.state);

  const refresh = async () => {
    await Promise.all([
      qc.invalidateQueries({ queryKey: ['epics'] }),
      qc.invalidateQueries({ queryKey: ['epics-counts'] }),
      qc.invalidateQueries({ queryKey: ['epics-board'] }),
    ]);
  };

  const doUnarchive = async (e: React.MouseEvent) => {
    e.stopPropagation();
    setBusy(true); setErr(null);
    try { await unarchiveEpic(epic.epicRef); await refresh(); }
    catch (ex) { setErr((ex as Error).message); }
    finally { setBusy(false); }
  };

  const onArchiveClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (needsReason) { setOpen(true); return; }
    void confirmArchive();
  };

  const confirmArchive = async () => {
    setBusy(true); setErr(null);
    try {
      await archiveEpic(epic.epicRef, needsReason ? reason.trim() : undefined);
      setOpen(false); setReason('');
      await refresh();
    } catch (ex) { setErr((ex as Error).message); }
    finally { setBusy(false); }
  };

  const btnCls =
    'text-2xs font-medium rounded border px-2 py-1 transition-colors disabled:opacity-50 ' +
    'border-slate-300 bg-white text-slate-600 hover:bg-slate-50';

  return (
    <>
      {archived ? (
        <button type="button" className={btnCls} disabled={busy} onClick={doUnarchive}>
          {busy ? '…' : 'Unarchive'}
        </button>
      ) : (
        <button type="button" className={btnCls} disabled={busy} onClick={onArchiveClick}>
          {busy ? '…' : 'Archive'}
        </button>
      )}
      {err && !open && <span className="ml-2 text-2xs text-rose-600">{err}</span>}

      {open && createPortal(
        // Modal overlay, portaled to <body> so it doesn't inherit the table
        // cell's `whitespace-nowrap` (which made the paragraph overflow) or any
        // table containing-block / z-index quirks. stopPropagation keeps clicks +
        // key events (Space/Enter) from bubbling to the row's nav handlers —
        // synthetic events still bubble through the React tree from a portal.
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 p-4 whitespace-normal"
          onMouseDown={(e) => { downOnBackdrop.current = e.target === e.currentTarget; }}
          onClick={(e) => {
            e.stopPropagation();
            if (e.target === e.currentTarget && downOnBackdrop.current && !busy) setOpen(false);
            downOnBackdrop.current = false;
          }}
          onKeyDown={(e) => e.stopPropagation()}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-label="Archive epic"
            className="w-full max-w-md rounded-xl bg-white p-5 shadow-xl space-y-3 whitespace-normal"
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => e.stopPropagation()}
          >
            <h3 className="text-sm font-semibold text-slate-800">
              Archive epic #{epic.number}
            </h3>
            <p className="text-xs text-slate-600">
              This epic isn’t complete ({epic.state}). A reason is required — it’s kept on the
              record and shown when you view archived epics. Stats are unaffected.
            </p>
            <textarea
              autoFocus
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Why are you archiving this? (e.g. superseded by epic #45, abandoned approach)"
              rows={3}
              className="w-full text-sm rounded-lg border border-slate-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary-300"
            />
            {err && <div className="text-2xs text-rose-600">{err}</div>}
            <div className="flex justify-end gap-2">
              <button
                type="button"
                className="text-xs rounded border border-slate-300 bg-white px-3 py-1.5 hover:bg-slate-50"
                onClick={() => { setOpen(false); setErr(null); }}
                disabled={busy}
              >
                Cancel
              </button>
              <button
                type="button"
                className="text-xs rounded bg-rose-600 text-white px-3 py-1.5 hover:bg-rose-700 disabled:opacity-50"
                onClick={confirmArchive}
                disabled={busy || reason.trim().length === 0}
              >
                {busy ? 'Archiving…' : 'Archive'}
              </button>
            </div>
          </div>
        </div>,
        document.body,
      )}
    </>
  );
};

export default ArchiveControl;
